import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import api from '@/services/ApiService';
import files from '@/services/FileService';
import { toast } from 'react-toastify';
import createItemLabel from '@/services/utils/labels';
import proxy from '@/services/ProxyService';

async function printPdf(label, printer, quantity) {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 10);
  const isoString = now.toISOString();
  const formattedString = isoString.replace('Z', '+00:00');

  const randFilename = Array.from(
    crypto.getRandomValues(new Uint8Array(16)),
    (num) => num.toString(16).padStart('0', 2),
  ).join('');

  const { data: file } = await api.post('/file', {
    filename: `labels/${randFilename}.pdf`,
    expiresAt: formattedString,
    public: true,
  });
  if (file.acknowledged) {
    const fileId = file.insertedId;
    const formData = new FormData();
    formData.append('file', label);
    const { data: fileUpload } = await files.post(`/${fileId}`, formData);
    if (fileUpload) {
      const { data: printJob } = await api.post('/printjob', {
        file: `${file.insertedId}`,
        printer: printer.id,
        quantity: parseInt(quantity, 10),
        public: true,
        sharedWith: [
          {
            type: 'role',
            permissions: {
              update: true,
              read: true,
            },
            id: printer.role,
          },
        ],
      });
      return printJob;
    }
  }
  return null;
}

export default function ArticleItems({ data }) {
  const customer =
    data.deliveryAddress || data.recordAddress || data.invoiceAddress;

  const { config } = useRouteLoaderData('auth');

  const [print, setPrint] = useState([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const fullItems = [];
    const items =
      data?.orderItems || data?.shipmentItems || data?.salesInvoiceItems;
    (async () => {
      await Promise.all(
        items.map(async (i) => {
          if (!i.articleId) return;
          const { data: article } = await proxy.get(
            `${config.warehouse.weclappInterface}/article/id/${i.articleId}`,
          );
          fullItems.push({
            id: i.id,
            quantity: i.quantity,
            title: i.title,
            print: article.articleType === 'STORABLE',
            description: i.description,
            articleNumber: i.articleNumber,
            storable: article.articleType === 'STORABLE',
            positionNumber: i.positionNumber,
          });
        }),
      );
      setPrint(fullItems.sort((a, b) => a.positionNumber - b.positionNumber));
    })();
  }, [data]);

  useEffect(() => {
    if (print.filter((i) => i.print).length === print.length) {
      setChecked(true);
    } else {
      setChecked(false);
    }
  }, [print]);

  function selectAll(boolValue) {
    setChecked(boolValue);
    const updatedPrint = print?.map((item) => {
      item.print = boolValue;
      return item;
    });
    setPrint(updatedPrint);
  }

  async function printLabels() {
    const res = await Promise.allSettled(
      print.map((item) => {
        if (!item.print) return;
        const labelPDF = createItemLabel(
          { l1Content: `Kd.: ${data.customerNumber}` },
          {
            l2Content: `${data.salesOrderNumber || data.orderNumber ? 'Auf.' : 'Li.'}: ${data.salesOrderNumber || data.orderNumber || data.shipmentNumber}`,
          },
          { l3Content: customer?.company?.replace(/\r?\n/g, ' ') },
          { l4Content: item.title },
          { l5Content: item.articleNumber },
        );
        const printer = config?.warehouse?.printer[data?.warehouseId];

        if (!printer?.id || !printer?.role) {
          toast.error(`Drucker ${data?.warehouseId} nicht gefunden`);
          return;
        }
        return printPdf(labelPDF, printer, item.quantity);
      }),
    );
    if (res.some((r) => r.status === 'rejected')) {
      toast.error('Error printing some labels');
    } else {
      toast.success('Labels printed');
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
      }}
    >
      <Typography variant="h4" gutterBottom>
        {data.shipmentNumber && 'Lieferung'}
        {data.orderNumber && 'Auftrag'}
        {data.invoiceNumber && 'Rechnung'}{' '}
        {data.shipmentNumber || data.orderNumber || data.invoiceNumber}
      </Typography>
      <FormControlLabel
        label="Alle auswählen"
        sx={{ ml: 2 }}
        control={
          <Checkbox
            checked={checked}
            sx={{ mr: 2 }}
            onChange={(evt) => {
              selectAll(evt.target.checked);
            }}
          />
        }
      />
      <List sx={{ width: '100%', overflowY: 'auto' }}>
        {print?.map((item) => (
          <ListItem key={item.id} sx={{ width: '100%' }}>
            <Checkbox
              sx={{ mr: 2 }}
              checked={item.print}
              onChange={(evt) => {
                setChecked(false);
                if (evt.target.checked) {
                  const p = [...print];
                  const index = p.findIndex((i) => i.id === item.id);
                  p[index].print = true;
                  setPrint(p);
                } else {
                  const p = [...print];
                  const index = p.findIndex((i) => i.id === item.id);
                  p[index].print = false;
                  setPrint(p);
                }
              }}
              inputProps={{ 'aria-label': 'controlled' }}
            />
            <ListItemText
              primary={item.title}
              secondary={item.articleNumber}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            />
            <input
              value={item.quantity}
              style={{ width: '60px', padding: '10px', marginLeft: '20px' }}
              type="number"
              min={0}
              onChange={(e) => {
                const p = [...print];
                const index = p.findIndex((i) => i.id === item.id);
                p[index].quantity = e.target.value;
                setPrint(p);
              }}
            />
          </ListItem>
        ))}
      </List>
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3 }}
          onClick={printLabels}
        >
          Drucken
        </Button>
    </Box>
  );
}
